import { useState, useEffect, useRef } from 'react'
import { X, ChevronDown, Check } from 'lucide-react'
import { cn } from '../../lib/utils'
import { COUNTRY_OPTIONS } from '../../lib/countries'

interface CountrySelectorProps {
  selectedCountries: string[]
  onCountriesChange: (countries: string[]) => void
  maxCountries?: number
}

export function CountrySelector({
  selectedCountries,
  onCountriesChange,
  maxCountries,
}: CountrySelectorProps) {
  const limit = typeof maxCountries === 'number' ? maxCountries : Infinity
  const hasLimit = Number.isFinite(limit)

  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const toggleCountry = (countryId: string) => {
    if (selectedCountries.includes(countryId)) {
      onCountriesChange(selectedCountries.filter((c) => c !== countryId))
    } else if (selectedCountries.length < limit) {
      onCountriesChange([...selectedCountries, countryId])
    }
  }

  const removeCountry = (countryId: string) => {
    onCountriesChange(selectedCountries.filter((c) => c !== countryId))
  }

  const getCountryName = (countryId: string) =>
    COUNTRY_OPTIONS.find((country) => country.id === countryId)?.name ||
    countryId

  const filteredCountries = COUNTRY_OPTIONS.filter((country) =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const handleInputFocus = () => setIsOpen(true)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setIsOpen(true)
  }

  return (
    <div className="space-y-4">
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <div className="min-h-[40px] w-full px-3 py-2 bg-white border border-neutral-200 rounded-md focus-within:border-neutral-800">
            <div className="flex flex-wrap gap-1 items-center">
              {selectedCountries.map((countryId) => (
                <div
                  key={countryId}
                  className="flex items-center gap-1 bg-brand-primary text-white px-2 py-1 rounded text-xs"
                >
                  <span>{getCountryName(countryId)}</span>
                  <button
                    onClick={() => removeCountry(countryId)}
                    className="hover:bg-white/20 rounded-full p-0.5"
                    type="button"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <input
                type="text"
                value={searchTerm}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                placeholder={
                  selectedCountries.length === 0
                    ? 'Search or select restrictions'
                    : ''
                }
                className="flex-1 min-w-[120px] outline-none bg-transparent text-sm"
                disabled={hasLimit && selectedCountries.length >= limit}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-neutral-100 rounded"
          >
            <ChevronDown
              className={cn(
                'w-4 h-4 transition-transform text-neutral-950',
                isOpen && 'rotate-180',
              )}
            />
          </button>
        </div>
        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white text-neutral-950 border border-neutral-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => {
                const isSelected = selectedCountries.includes(country.id)
                const isDisabled =
                  !isSelected && hasLimit && selectedCountries.length >= limit
                return (
                  <button
                    key={country.id}
                    type="button"
                    onClick={() => {
                      if (!isDisabled) toggleCountry(country.id)
                    }}
                    disabled={isDisabled}
                    className={cn(
                      'w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex items-center justify-between',
                      isSelected && 'bg-brand-primary/5 text-brand-primary',
                      isDisabled && 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    <span>{country.name}</span>
                    {isSelected && <Check className="w-4 h-4" />}
                  </button>
                )
              })
            ) : (
              <div className="px-3 py-2 text-sm text-neutral-500">
                No countries found
              </div>
            )}
          </div>
        )}
      </div>
      <p className="text-xs text-neutral-600">
        {hasLimit
          ? `${selectedCountries.length}/${limit} restrictions selected`
          : `${selectedCountries.length} restriction${
              selectedCountries.length === 1 ? '' : 's'
            } selected`}
      </p>
    </div>
  )
}
